/** A Geotab zone, reduced to what is needed to say whether something is inside it. */
export interface ZoneOutline {
  id: string;
  name: string;
  /** The corners, as Geotab gives them: `x` is the longitude, `y` the latitude. */
  points: { x: number; y: number }[];
}

/**
 * Whether a position lies inside an outline.
 *
 * Ray casting: count how often a ray from the point crosses the edges, and an odd count means
 * inside. Longitude and latitude are treated as plain coordinates, which is accurate enough for
 * zones of the size a depot or a customer site has.
 */
export function isInsideZone(longitude: number, latitude: number, zone: ZoneOutline): boolean {
  const { points } = zone;
  if (points.length < 3) {
    return false;
  }

  let inside = false;
  for (let current = 0, previous = points.length - 1; current < points.length; previous = current++) {
    const { x: currentX, y: currentY } = points[current];
    const { x: previousX, y: previousY } = points[previous];

    const straddles = currentY > latitude !== previousY > latitude;
    if (!straddles) {
      continue;
    }

    const crossingX =
      previousY === currentY
        ? currentX
        : currentX + ((latitude - currentY) / (previousY - currentY)) * (previousX - currentX);

    if (longitude < crossingX) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Roughly how much ground an outline covers, for comparing two of them.
 *
 * The shoelace formula in degrees. Not an area in any unit anybody would quote — it only has to
 * order two zones by size.
 */
function outlineSize(zone: ZoneOutline): number {
  const { points } = zone;
  let doubled = 0;
  for (let current = 0, previous = points.length - 1; current < points.length; previous = current++) {
    doubled +=
      points[previous].x * points[current].y - points[current].x * points[previous].y;
  }
  return Math.abs(doubled) / 2;
}

/**
 * The zone a position is in.
 *
 * Zones nest — a loading bay inside a yard inside a site — and the smallest one a vehicle is in is
 * the one that says the most about where it is, so that is the one returned.
 *
 * @param longitude - Where the vehicle is.
 * @param latitude - Where the vehicle is.
 * @param zones - The zones to consider.
 * @returns The most specific zone containing the position, or `undefined`.
 */
export function zoneAt(
  longitude: number,
  latitude: number,
  zones: readonly ZoneOutline[],
): ZoneOutline | undefined {
  let smallest: ZoneOutline | undefined;
  let smallestSize = Number.POSITIVE_INFINITY;

  for (const zone of zones) {
    if (!isInsideZone(longitude, latitude, zone)) {
      continue;
    }

    const size = outlineSize(zone);
    if (size < smallestSize) {
      smallest = zone;
      smallestSize = size;
    }
  }

  return smallest;
}
